
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PEOProviderSearch } from './PEOProviderSearch';
import type { Application } from '@/types/database.types';

interface EntityStatusProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  readOnly?: boolean;
}

const PAYROLL_PROVIDERS = [
  "ADP",
  "Paychex",
  "Gusto",
  "QuickBooks Payroll",
  "Rippling",
  "Paylocity",
  "TriNet",
  "Zenefits",
  "Workday",
  "Paycom",
  "Square Payroll",
  "OnPay",
  "Patriot Software",
  "Sage",
  "Other"
];

export const EntityStatus = ({ register, watch, setValue, readOnly = false }: EntityStatusProps) => {
  const isNonProfit = watch('is_non_profit');
  const isOnPEO = watch('current_peo');
  const currentPayrollPlatform = watch('current_payroll_platform');

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center justify-between space-x-2">
        <div className="space-y-0.5">
          <Label htmlFor="is_non_profit">This entity is a 501(c)3 non-profit</Label>
        </div>
        <Switch 
          id="is_non_profit"
          checked={isNonProfit}
          onCheckedChange={(checked) => setValue('is_non_profit', checked)}
          {...register('is_non_profit')}
          disabled={readOnly}
        />
      </div>
      
      <div className="space-y-2">
        <Label>This Entity is Currently on a PEO</Label>
        <RadioGroup 
          value={isOnPEO ? "yes" : isOnPEO === false ? "no" : undefined}
          onValueChange={(value) => setValue('current_peo', value === "yes")}
          className="flex flex-col space-y-1"
          disabled={readOnly}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="yes" id="peo-yes" disabled={readOnly} />
            <Label htmlFor="peo-yes" className="font-normal">Yes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="no" id="peo-no" disabled={readOnly} />
            <Label htmlFor="peo-no" className="font-normal">No</Label>
          </div>
        </RadioGroup>
      </div>
      
      {isOnPEO && (
        <div className="space-y-2 mt-4 pt-2 border-t border-gray-100">
          <PEOProviderSearch 
            initialProvider={watch('current_provider')} 
            onSelectProvider={(provider) => setValue('current_provider', provider)}
            readOnly={readOnly} 
          />
        </div>
      )}
      
      {isOnPEO === false && (
        <div className="space-y-2 mt-4 pt-2 border-t border-gray-100">
          <Label htmlFor="current_payroll_platform">Current Payroll Provider</Label>
          <Select
            value={currentPayrollPlatform || ""}
            onValueChange={(value) => {
              console.log("Setting payroll platform to:", value);
              // Don't set empty strings to maintain consistent null values
              setValue('current_payroll_platform', value === "select_provider" ? null : value);
            }}
            disabled={readOnly}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="Select a payroll provider" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {/* Changed from "" to "select_provider" */}
              <SelectItem value="select_provider">Select a provider</SelectItem>
              {PAYROLL_PROVIDERS.map((provider) => (
                <SelectItem key={provider} value={provider}>{provider}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};
